import { FormStepModel, useFormStepper } from "components/form-stepper/components/form-stepper";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Button } from "@material-ui/core";
import { ConfirmarIcon } from "components/icons/confirmar-icon";
import { AvancarIcon } from "components/icons/avancar-icon";
import { useNotificacoes } from "components";
import { VoltarIcon } from "utils";
import { NomeIcon } from "components/icons/nome-icon";
import { TelefoneIcon } from "components/icons/telefone-icon";
import { CalendarioIcon } from "components/icons/calendario-icon";
import { CampoIdentificacao } from "./campo-identificacao/campo-identificacao";
import { EnumSexo, PessoaModelForm } from "api/cardapio/models/pessoa/pessoa-model";
import { SexoIcon } from "components/icons/sexo-icon";
import { SexoIdentificacao } from "./sexo-identificacao/sexo-identificacao";
import { IdentificacaoIcon } from "components/icons/identificacao-icon";
import { DefaultFormRefs } from "components/forms/utils";
import { EmailIcon } from "components/icons/email-icon";
import { removerMascaraDocumento } from "utils/cpfcnpj-format";

interface HookIdentificacaoFragmentProps {
    soliClientesPorModeloTrabalho: number
    submitNovaPessoa: (model: PessoaModelForm) => Promise<void>
    isDelivery: boolean
    steps: EnumTelaIdentificacao[]
    empresaId: string
    faseIdentificacao: boolean
}

export enum EnumTelaIdentificacao {
    CpfCnpj = 1,
    Nome = 2,
    Telefone = 4,
    DataNascimento = 8,
    Sexo = 16,
    Email = 32,
    Todos = 17,
}

export interface FormClienteProps {
    cpfCnpj: string;
    nome: string;
    telefone: string;
    dataNascimento: string;
    sexo: EnumSexo;
    email: string;
}

export const useIdentificacaoFragment = ({ submitNovaPessoa, isDelivery, steps, empresaId, faseIdentificacao }: HookIdentificacaoFragmentProps) => {
    const { currentStep, nextStep, prevStep } = useFormStepper(5);
    const { showErrorMessage } = useNotificacoes()
    const qtdEtapas = useRef<number>(0)

    const formClienteValue = useRef<FormClienteProps>({
        cpfCnpj: '',
        nome: '',
        telefone: '',
        dataNascimento: '',
        sexo: EnumSexo.NaoInformado,
        email: '',
    })

    const refPreCadastroCpfCnpj =
        useRef<DefaultFormRefs<string>>(null);
    const refPreCadastroNome =
        useRef<DefaultFormRefs<string>>(null);
    const refPreCadastroTelefone =
        useRef<DefaultFormRefs<string>>(null);
    const refPreCadastroDataNascimento =
        useRef<DefaultFormRefs<string>>(null);
    const refPreCadastroEmail =
        useRef<DefaultFormRefs<string>>(null);

    useEffect(() => {
        if (faseIdentificacao && isDelivery) {
            formClienteValue.current = {
                cpfCnpj: '',
                nome: '',
                telefone: '',
                dataNascimento: '',
                sexo: EnumSexo.NaoInformado,
                email: '',
            }
        }
    }, [faseIdentificacao, isDelivery])


    const retTelaIndex = useCallback(
        (index: number): EnumTelaIdentificacao | undefined => {
            let stepObj = {}

            steps.forEach((step, index) => {
                stepObj = {
                    ...stepObj,
                    [index]: step
                }
            })

            const retornaStepObj = (index: number) => ({
                ...stepObj
            })[index] || undefined

            return retornaStepObj(index)

        }, [steps],
    );

    const handleSubmitIdentificacao = useCallback(async () => {
        submitNovaPessoa({
            cpfcnpj: formClienteValue.current.cpfCnpj,
            nome: formClienteValue.current.nome,
            telefone: formClienteValue.current.telefone,
            dtNasc: formClienteValue.current.dataNascimento,
            email: formClienteValue.current.email,
            sexo: formClienteValue.current.sexo
        })
    }, [submitNovaPessoa])

    const avancarStep = useCallback(async () => {
        try {
            const handleSexo = () => {
                if (qtdEtapas.current - 1 === currentStep) {
                    handleSubmitIdentificacao()
                }
            }
            const currentEtapaEnum = retTelaIndex(currentStep)
            if (currentEtapaEnum) {

                switch (currentEtapaEnum) {
                    case EnumTelaIdentificacao.CpfCnpj:
                        refPreCadastroCpfCnpj.current?.submitForm();
                        break
                    case EnumTelaIdentificacao.Nome:
                        refPreCadastroNome.current?.submitForm();
                        break
                    case EnumTelaIdentificacao.Telefone:
                        refPreCadastroTelefone.current?.submitForm();
                        break
                    case EnumTelaIdentificacao.Email:
                        refPreCadastroEmail.current?.submitForm();
                        break
                    case EnumTelaIdentificacao.DataNascimento:
                        refPreCadastroDataNascimento.current?.submitForm();
                        break
                    case EnumTelaIdentificacao.Sexo:
                        handleSexo()
                        break
                }
            }
        } catch (error: any) {
            showErrorMessage(error.message)
        }
    }, [retTelaIndex, currentStep, handleSubmitIdentificacao, showErrorMessage]);

    const voltarButton = useCallback(() => {
        return (
            <Button
                type="submit"
                color="primary"
                variant="outlined"
                fullWidth
                size="large"
                onClick={async () => {
                    prevStep();
                }}
            >
                <VoltarIcon tipo='BUTTON' />
                Voltar
            </Button>
        );
    }, [prevStep]);

    const avancarButton = useCallback(() => {
        return (
            <Button
                type="submit"
                color="primary"
                variant="contained"
                fullWidth
                size="large"
                onClick={() => avancarStep()}
            >
                {(qtdEtapas.current - 1 === currentStep) ? (
                    <>
                        <ConfirmarIcon tipo="BUTTON_PRIMARY" />
                        Confirmar
                    </>
                ) : (
                    <>
                        <AvancarIcon tipo="BUTTON_PRIMARY" />
                        Avançar
                    </>
                )}
            </Button>
        );
    }, [avancarStep, currentStep]);

    const handleSubmitForm = useCallback(async (
        modelo: any,
    ) => {
        const key = Object.keys(modelo)[0]
        if (key === 'cpfCnpj') {
            formClienteValue.current.cpfCnpj = removerMascaraDocumento(modelo.cpfCnpj);
        }
        if (key === 'nome') {
            formClienteValue.current.nome = modelo.nome;
        }
        if (key === 'email') {
            formClienteValue.current.email = modelo.email;
        }
        if (key === 'telefone') {
            formClienteValue.current.telefone = modelo.telefone;
        }
        if (key === 'dataNascimento') {
            formClienteValue.current.dataNascimento = modelo.dataNascimento;
        }
        try {
            if (currentStep < (qtdEtapas.current - 1)) {
                nextStep()
            } else {
                handleSubmitIdentificacao()
            }
        } catch (error: any) {
            showErrorMessage(error.message)
        }
    }, [currentStep, handleSubmitIdentificacao, nextStep, showErrorMessage]);

    const getFormPreCadastroCpfCnpj = useMemo(() => {

        return (
            <CampoIdentificacao
                name="cpfCnpj"
                label="CPF/CNPJ"
                tipo="CNPJ_CPF"
                loading={false}
                showLoading={false}
                onSubmit={handleSubmitForm}
                ref={refPreCadastroCpfCnpj}
            />
        )
    }, [handleSubmitForm])

    const getFormPreCadastroTelefone = useMemo(() => {
        return (
            <CampoIdentificacao
                name="telefone"
                label="Telefone"
                tipo="TELEFONE"
                loading={false}
                showLoading={false}
                isNotOpcional={isDelivery}
                onSubmit={handleSubmitForm}
                ref={refPreCadastroTelefone}
            />
        )
    }, [handleSubmitForm, isDelivery])

    const getFormPreCadastroEmail = useMemo(() => {
        return (
            <CampoIdentificacao
                name="email"
                label="Email"
                tipo="EMAIL"
                loading={false}
                showLoading={false}
                onSubmit={handleSubmitForm}
                ref={refPreCadastroEmail}
            />
        )
    }, [handleSubmitForm])

    const getFormPreCadastroNome = useMemo(() => {
        return (
            <CampoIdentificacao
                name="nome"
                label="Nome"
                tipo="TEXTO"
                loading={false}
                isNotOpcional={isDelivery}
                showLoading={false}
                onSubmit={handleSubmitForm}
                ref={refPreCadastroNome}
            />
        )
    }, [handleSubmitForm, isDelivery])

    const getFormPreCadastroDataNascimento = useMemo(() => {
        return (
            <CampoIdentificacao
                name="dataNascimento"
                label="Data de Nascimento"
                tipo="DATA"
                loading={false}
                showLoading={false}
                onSubmit={handleSubmitForm}
                ref={refPreCadastroDataNascimento}
            />
        )
    }, [handleSubmitForm])

    const getFormPreCadastroSexo = useCallback(() => {
        const valueChanged = async (text: EnumSexo) => {
            formClienteValue.current = {
                ...formClienteValue.current,
                sexo: text
            };
            return true;
        }
        return (
            <SexoIdentificacao
                valueChanged={valueChanged}
            />
        )
    }, [])

    useEffect(() => {
        switch (retTelaIndex(currentStep)) {
            case EnumTelaIdentificacao.Nome:
                refPreCadastroNome.current?.fillForm(formClienteValue.current.nome);
                break;
            case EnumTelaIdentificacao.CpfCnpj:
                refPreCadastroCpfCnpj.current?.fillForm(formClienteValue.current.cpfCnpj);
                break;
            case EnumTelaIdentificacao.Telefone:
                refPreCadastroTelefone.current?.fillForm(
                    formClienteValue.current.telefone
                );
                break;
            case EnumTelaIdentificacao.Email:
                refPreCadastroEmail.current?.fillForm(
                    formClienteValue.current.email
                );
                break;
            case EnumTelaIdentificacao.DataNascimento:
                refPreCadastroDataNascimento.current?.fillForm(
                    formClienteValue.current.dataNascimento
                );
                break;
        }
    }, [currentStep, retTelaIndex]);

    const getFormArray = useMemo(() => {
        const ret: FormStepModel[] = [];

        let i = 0;
        while (retTelaIndex(i)) {
            const tela = retTelaIndex(i);
            switch (tela) {
                case EnumTelaIdentificacao.Telefone:
                    ret.push(
                        new FormStepModel(
                            'Telefone',
                            `Para meios de contato, informe o telefone.`,
                            <TelefoneIcon tipo="GERAL" />,
                            'Telefone',
                            <TelefoneIcon tipo="GERAL" />,
                            getFormPreCadastroTelefone,
                            voltarButton(),
                            avancarButton(),
                        ),
                    );
                    break;
                case EnumTelaIdentificacao.CpfCnpj:
                    ret.push(
                        new FormStepModel(
                            'CPF/CNPJ',
                            `Informe o CPF ou CNPJ`,
                            <IdentificacaoIcon tipo="GERAL" />,
                            'CPF/CNPJ',
                            <IdentificacaoIcon tipo="GERAL" />,
                            getFormPreCadastroCpfCnpj,
                            voltarButton(),
                            avancarButton(),
                        ),
                    );
                    break;
                case EnumTelaIdentificacao.Nome:
                    ret.push(
                        new FormStepModel(
                            'Nome',
                            `Informe como você gostaria de ser identificado`,
                            <NomeIcon tipo="GERAL" />,
                            'Nome',
                            <NomeIcon tipo="GERAL" />,
                            getFormPreCadastroNome,
                            voltarButton(),
                            avancarButton(),
                        ),
                    );
                    break;
                case EnumTelaIdentificacao.Email:
                    ret.push(
                        new FormStepModel(
                            'E-mail',
                            `Informe o e-mail`,
                            <EmailIcon tipo="GERAL" />,
                            'E-mail',
                            <EmailIcon tipo="GERAL" />,
                            getFormPreCadastroEmail,
                            voltarButton(),
                            avancarButton(),
                        ),
                    );
                    break;
                case EnumTelaIdentificacao.DataNascimento:
                    ret.push(
                        new FormStepModel(
                            'Data de Nascimento',
                            `Informe a Data de Nascimento`,
                            <CalendarioIcon tipo="GERAL" />,
                            'Data de Nascimento',
                            <CalendarioIcon tipo="GERAL" />,
                            getFormPreCadastroDataNascimento,
                            voltarButton(),
                            avancarButton(),
                        ),
                    );
                    break;
                case EnumTelaIdentificacao.Sexo:
                    ret.push(
                        new FormStepModel(
                            'Gênero',
                            `Informe o seu gênero`,
                            <SexoIcon tipo="GERAL" />,
                            'Gênero',
                            <SexoIcon tipo="GERAL" />,
                            getFormPreCadastroSexo(),
                            voltarButton(),
                            avancarButton(),
                        ),
                    );
                    break;
            }
            i++;
        }
        if (ret.length > 0) {
            ret[ret.length - 1].previousButton = voltarButton();
            ret[ret.length - 1].nextButton = avancarButton();
        }
        qtdEtapas.current = ret.length

        return ret;
    }, [avancarButton, getFormPreCadastroCpfCnpj, getFormPreCadastroDataNascimento, getFormPreCadastroEmail, getFormPreCadastroNome, getFormPreCadastroSexo, getFormPreCadastroTelefone, retTelaIndex, voltarButton]);

    return {
        formStepper: {
            currentStep,
            nextStep,
            prevStep,
        },
        formArray: getFormArray,
    }
}